import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Sparkles, Building2, ChevronRight, ChevronLeft, Check, 
  Loader2, Clock, MessageSquare, Sliders, FileText,
  AlertCircle, CheckCircle2, BriefcaseBusiness
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';

const OnboardingPage = () => {
  const { user, token, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRerun = searchParams.get('rerun') === 'true';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Options from API
  const [industries, setIndustries] = useState([]);
  const [toneOptions, setToneOptions] = useState([]);
  const [responseStyleOptions, setResponseStyleOptions] = useState([]);
  const [placeholders, setPlaceholders] = useState({});
  
  // Step 1: Industry
  const [industry, setIndustry] = useState('');
  
  // Step 2: Business Description
  const [businessProfile, setBusinessProfile] = useState('');
  
  // Step 3: Services
  const [servicesInput, setServicesInput] = useState('');
  const [services, setServices] = useState([]);
  
  // Step 4: Operating Hours
  const [operatingHours, setOperatingHours] = useState({
    monday: { enabled: true, start: '09:00', end: '18:00' },
    tuesday: { enabled: true, start: '09:00', end: '18:00' },
    wednesday: { enabled: true, start: '09:00', end: '18:00' },
    thursday: { enabled: true, start: '09:00', end: '18:00' },
    friday: { enabled: true, start: '09:00', end: '18:00' },
    saturday: { enabled: false, start: '09:00', end: '14:00' },
    sunday: { enabled: false, start: '09:00', end: '14:00' }
  });
  
  // Step 5: AI Tone & Style
  const [tone, setTone] = useState('friendly');
  const [responseStyle, setResponseStyle] = useState('concise');
  
  // AI Settings
  const [aiEnabled, setAiEnabled] = useState(true);
  const [humanInLoop, setHumanInLoop] = useState(true);

  const totalSteps = 5;

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Fetch industries and options
      const indResp = await axios.get(`${API}/onboarding/industries`);
      setIndustries(indResp.data.industries || []);
      setToneOptions(indResp.data.tone_options || []);
      setResponseStyleOptions(indResp.data.response_style_options || []);
      
      // If rerun, fetch existing data
      if (isRerun || true) {
        try {
          const statusResp = await axios.get(`${API}/onboarding/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (statusResp.data.industry) {
            setIndustry(statusResp.data.industry);
            fetchPlaceholdersForIndustry(statusResp.data.industry);
          }
          
          // Pre-fill existing data
          const existingData = statusResp.data.existing_data || {};
          if (existingData.business_profile) {
            setBusinessProfile(existingData.business_profile);
          }
          // Feb-2026 horizontal-SaaS fix: services can be saved as plain
          // strings (Clear Dental) OR as objects with name/description
          // (Osso). Normalize to display strings BEFORE setState so the
          // <Badge>{service}</Badge> render at L336 never receives an
          // object (React: "Objects are not valid as a React child").
          const normalizeServiceItem = (s) => {
            if (s == null) return '';
            if (typeof s === 'string') return s.trim();
            if (typeof s === 'number' || typeof s === 'boolean') return String(s);
            if (typeof s === 'object') {
              const nameKeys = ['name', 'title', 'service', 'label', 'value', 'key'];
              const descKeys = ['description', 'desc', 'summary', 'details', 'subtitle'];
              let nm = '';
              for (const k of nameKeys) {
                if (typeof s[k] === 'string' && s[k].trim()) { nm = s[k].trim(); break; }
                if (typeof s[k] === 'number' || typeof s[k] === 'boolean') { nm = String(s[k]); break; }
              }
              let desc = '';
              for (const k of descKeys) {
                if (typeof s[k] === 'string' && s[k].trim()) { desc = s[k].trim(); break; }
              }
              if (nm && desc) return `${nm} — ${desc}`;
              return nm || desc || '';
            }
            try { return String(s).trim(); } catch { return ''; }
          };
          const rawServices = Array.isArray(existingData.services)
            ? existingData.services
            : (existingData.services ? [existingData.services] : []);
          const normalizedServices = rawServices
            .map(normalizeServiceItem)
            .filter((s) => s && s.length > 0);
          if (normalizedServices.length > 0) {
            setServices(normalizedServices);
            setServicesInput(normalizedServices.join(', '));
          }
          if (existingData.operating_hours) {
            setOperatingHours(existingData.operating_hours);
          }
          if (existingData.tone) {
            setTone(existingData.tone);
          }
          if (existingData.response_style) {
            setResponseStyle(existingData.response_style);
          }
        } catch (e) {
          // First-time setup, no existing data
        }
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaceholdersForIndustry = async (ind) => {
    try {
      const response = await axios.get(`${API}/onboarding/questions/${ind}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaceholders(response.data.placeholders || {});
    } catch (error) {
      console.error('Failed to fetch placeholders:', error);
    }
  };

  const handleIndustryChange = (value) => {
    setIndustry(value);
    fetchPlaceholdersForIndustry(value);
  };

  const handleServicesInputChange = (value) => {
    setServicesInput(value);
    // Parse comma-separated services
    const parsed = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    setServices(parsed);
  };

  const handleHoursChange = (day, field, value) => {
    setOperatingHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const handleApply = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/onboarding/apply`, {
        industry,
        business_profile: businessProfile,
        services,
        operating_hours: operatingHours,
        tone,
        response_style: responseStyle,
        ai_enabled: aiEnabled,
        human_in_loop: humanInLoop
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const faqsMsg = response.data.faqs_synced > 0 
        ? `Updated ${response.data.faqs_synced} FAQ entries.` 
        : '';
      
      toast.success(`AI Setup complete! ${faqsMsg}`);
      
      // Refresh user data
      if (fetchUser) await fetchUser();
      
      navigate('/dashboard/ai-setup');
    } catch (error) {
      console.error('Failed to apply onboarding:', error);
      toast.error('Failed to apply setup. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/onboarding/skip`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      // Ignore skip errors
    }
    navigate('/dashboard');
    toast.info('You can complete setup anytime from AI Setup.');
  };

  const nextStep = () => {
    if (step === 1 && !industry) {
      toast.error('Please select an industry');
      return;
    }
    if (step === 2 && !businessProfile.trim()) {
      toast.error('Please describe your business');
      return;
    }
    if (step === 3 && services.length === 0) {
      toast.error('Please enter at least one service');
      return;
    }
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const stepInfo = [
    { icon: Building2, title: 'Industry', desc: 'Select your business type' },
    { icon: FileText, title: 'About Your Business', desc: 'Describe what you do' },
    { icon: BriefcaseBusiness, title: 'Services', desc: 'List your offerings' },
    { icon: Clock, title: 'Operating Hours', desc: 'Set your schedule' },
    { icon: Sliders, title: 'AI Personality', desc: 'Configure AI behavior' }
  ];

  return (
    <div className="max-w-3xl mx-auto py-8 px-4" data-testid="onboarding-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">AI Setup</h1>
        </div>
        <p className="text-gray-600">
          {isRerun ? 'Update your AI assistant configuration' : 'Configure your AI assistant in a few steps'}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8 px-4">
        {stepInfo.map((s, idx) => {
          const StepIcon = s.icon;
          const stepNum = idx + 1;
          const isActive = step === stepNum;
          const isComplete = step > stepNum;
          
          return (
            <div key={idx} className="flex flex-col items-center">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center mb-1
                ${isActive ? 'bg-primary text-white' : 
                  isComplete ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}
              `}>
                {isComplete ? <Check className="h-5 w-5" /> : <StepIcon className="h-5 w-5" />}
              </div>
              <span className={`text-xs ${isActive ? 'text-primary font-medium' : 'text-gray-500'}`}>
                {s.title}
              </span>
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {React.createElement(stepInfo[step - 1].icon, { className: "h-5 w-5 text-primary" })}
            Step {step}: {stepInfo[step - 1].title}
          </CardTitle>
          <CardDescription>{stepInfo[step - 1].desc}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: Industry Selection */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-industry">
              <Label>Select Your Industry</Label>
              <Select value={industry} onValueChange={handleIndustryChange}>
                <SelectTrigger data-testid="industry-select">
                  <SelectValue placeholder="Choose your industry..." />
                </SelectTrigger>
                <SelectContent>
                  {industries.map(ind => (
                    <SelectItem key={ind.id} value={ind.id}>{ind.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                This helps us customize AI responses for your business type.
              </p>
            </div>
          )}

          {/* Step 2: Business Description */}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-business-profile">
              <Label>Business Description</Label>
              <Textarea
                value={businessProfile}
                onChange={(e) => setBusinessProfile(e.target.value)}
                placeholder={placeholders.placeholder_description || "Paste your About Us section here or describe your business in a few sentences..."}
                rows={6}
                data-testid="business-profile-input"
              />
              <p className="text-sm text-gray-500">
                This description helps the AI understand your business and provide relevant responses to customers.
              </p>
            </div>
          )}

          {/* Step 3: Services */}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-services">
              <Label>Your Services</Label>
              <Input
                value={servicesInput}
                onChange={(e) => handleServicesInputChange(e.target.value)}
                placeholder={placeholders.placeholder_services || "e.g., Product sales, Consultations, Home delivery, Support"}
                data-testid="services-input"
              />
              <p className="text-sm text-gray-500">
                Enter your services separated by commas. This creates a FAQ entry and helps AI answer service inquiries.
              </p>
              
              {services.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {services.map((service, idx) => (
                    <Badge key={idx} variant="secondary">{service}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Operating Hours */}
          {step === 4 && (
            <div className="space-y-4" data-testid="step-operating-hours">
              <p className="text-sm text-gray-500 mb-4">
                Set your business operating hours. Note: AI is always available 24/7, but this helps answer customer inquiries about when you're open.
              </p>
              
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <div key={day} className="flex items-center gap-4 py-2 border-b">
                  <div className="w-24 capitalize font-medium">{day}</div>
                  <Switch
                    checked={operatingHours[day]?.enabled}
                    onCheckedChange={(checked) => handleHoursChange(day, 'enabled', checked)}
                    data-testid={`hours-switch-${day}`}
                  />
                  {operatingHours[day]?.enabled ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={operatingHours[day]?.start || '09:00'}
                        onChange={(e) => handleHoursChange(day, 'start', e.target.value)}
                        className="w-28"
                      />
                      <span>to</span>
                      <Input
                        type="time"
                        value={operatingHours[day]?.end || '18:00'}
                        onChange={(e) => handleHoursChange(day, 'end', e.target.value)}
                        className="w-28"
                      />
                    </div>
                  ) : (
                    <span className="text-gray-400">Closed</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 5: AI Tone & Style */}
          {step === 5 && (
            <div className="space-y-6" data-testid="step-tone-style">
              <div className="space-y-3">
                <Label>AI Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="tone-select">
                    <SelectValue placeholder="Select tone..." />
                  </SelectTrigger>
                  <SelectContent>
                    {toneOptions.map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>
                        <div>
                          <span className="font-medium">{opt.label}</span>
                          <span className="text-gray-500 ml-2 text-sm">- {opt.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Response Style</Label>
                <Select value={responseStyle} onValueChange={setResponseStyle}>
                  <SelectTrigger data-testid="response-style-select">
                    <SelectValue placeholder="Select style..." />
                  </SelectTrigger>
                  <SelectContent>
                    {responseStyleOptions.map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>
                        <div>
                          <span className="font-medium">{opt.label}</span>
                          <span className="text-gray-500 ml-2 text-sm">- {opt.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4">AI Behavior Settings</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enable AI Responses</Label>
                      <p className="text-sm text-gray-500">AI will automatically respond to customer messages</p>
                    </div>
                    <Switch
                      checked={aiEnabled}
                      onCheckedChange={setAiEnabled}
                      data-testid="ai-enabled-switch"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Human-in-the-Loop</Label>
                      <p className="text-sm text-gray-500">Require human review for AI responses</p>
                    </div>
                    <Switch
                      checked={humanInLoop}
                      onCheckedChange={setHumanInLoop}
                      data-testid="hitl-switch"
                    />
                  </div>
                </div>
              </div>
              
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Configuration Summary
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Industry:</strong> {industries.find(i => i.id === industry)?.name || industry}</p>
                  <p><strong>Services:</strong> {services.length} defined</p>
                  <p><strong>Tone:</strong> {toneOptions.find(t => t.id === tone)?.label || tone}</p>
                  <p><strong>Style:</strong> {responseStyleOptions.find(s => s.id === responseStyle)?.label || responseStyle}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <div>
          {step > 1 ? (
            <Button variant="outline" onClick={prevStep} data-testid="prev-step-btn">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={handleSkip} data-testid="skip-btn">
              Skip for now
            </Button>
          )}
        </div>
        
        <div className="flex gap-2">
          {step < totalSteps ? (
            <Button onClick={nextStep} data-testid="next-step-btn">
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleApply} disabled={submitting} data-testid="apply-btn">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Rerun Notice */}
      {isRerun && (
        <Alert className="mt-6 bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-700">
            <strong>Updating Configuration:</strong> Changes will update your existing settings and FAQs. No duplicates will be created.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default OnboardingPage;
